import { supabase, publicSupabase, isConfigured } from './supabase';

export const LANDING_ASSET_BUCKET = 'landing-assets';
export const LANDING_ASSET_MAX_BYTES = 5 * 1024 * 1024;

export const LANDING_ASSET_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
];

export const LANDING_ASSET_SECTIONS = [
  { id: 'applications', label: 'Aplicativos de marca' },
  { id: 'illustrations', label: 'Ilustraciones de landing' },
  { id: 'projects', label: 'Proyectos (carrusel)' },
  { id: 'testimonials', label: 'Testimonios / casos' },
];

export const LANDING_PACKAGE_SCOPE_GROUPS = [
  {
    id: 'normal',
    label: 'Paquetes normales',
    options: [
      { id: 'starter', label: 'Starter', aliases: ['starter', 'normal_starter', 'branding_starter'] },
      { id: 'select', label: 'Select', aliases: ['select', 'identity', 'normal_select', 'branding_identity'] },
      { id: 'premium', label: 'Premium', aliases: ['premium', 'normal_premium', 'branding_legacy'] },
    ],
  },
  {
    id: 'corporate',
    label: 'Paquetes corporativos',
    options: [
      { id: 'corporate_essence', label: 'Essence', aliases: ['corporate_essence', 'essence'] },
      { id: 'corporate_legacy', label: 'Legacy', aliases: ['corporate_legacy'] },
      { id: 'corporate_ascend', label: 'Ascend', aliases: ['corporate_ascend', 'ascend'] },
    ],
  },
];

export const PACKAGE_SCOPE_OPTIONS = LANDING_PACKAGE_SCOPE_GROUPS.flatMap(group => group.options);

export const LANDING_ASSET_PRESETS = {
  applications: [
    {
      id: 'business_card',
      key: 'application_business_card',
      title: 'Tarjeta de presentación',
      alt_text: 'Fotografía de aplicación de marca en tarjeta de presentación',
      description: 'Uso de marca aplicado a tarjetas y contacto comercial.',
    },
    {
      id: 'packaging',
      key: 'application_packaging',
      title: 'Empaque',
      alt_text: 'Fotografía de aplicación de marca en empaque',
      description: 'Marca aplicada a empaque, bolsa, caja o etiqueta.',
    },
    {
      id: 'social_post',
      key: 'application_social_post',
      title: 'Post de redes sociales',
      alt_text: 'Visual de marca aplicado a redes sociales',
      description: 'Aplicación para publicaciones, historias o piezas digitales.',
    },
    {
      id: 'stationery',
      key: 'application_stationery',
      title: 'Papelería',
      alt_text: 'Fotografía de papelería corporativa con identidad de marca',
      description: 'Documentos, tarjetas, sobres o material institucional.',
    },
    {
      id: 'brand_presentation',
      key: 'application_brand_presentation',
      title: 'Presentación de marca',
      alt_text: 'Presentación comercial con identidad de marca',
      description: 'Deck, propuesta o documento comercial con sistema visual.',
    },
    {
      id: 'uniform',
      key: 'application_uniform',
      title: 'Uniforme',
      alt_text: 'Aplicación de marca en uniforme o indumentaria',
      description: 'Uso de marca en prendas, equipo o identificación interna.',
    },
    {
      id: 'menu',
      key: 'application_menu',
      title: 'Menú / carta',
      alt_text: 'Aplicación de marca en menú o carta',
      description: 'Pieza impresa o digital para oferta de productos/servicios.',
    },
    {
      id: 'letterhead',
      key: 'application_letterhead',
      title: 'Papel membretado',
      alt_text: 'Aplicación de marca en papel membretado',
      description: 'Formato documental para comunicaciones formales.',
    },
    {
      id: 'signage',
      key: 'application_signage',
      title: 'Señalética',
      alt_text: 'Aplicación de marca en señalética o espacio físico',
      description: 'Marca aplicada a local, oficina, punto de venta o dirección.',
    },
    {
      id: 'website',
      key: 'application_website',
      title: 'Website / landing',
      alt_text: 'Aplicación de marca en website o landing page',
      description: 'Identidad aplicada a una experiencia web.',
    },
  ],
  illustrations: [
    {
      id: 'hero',
      key: 'hero',
      title: 'Ilustración principal',
      alt_text: 'Ilustración principal de la cotización',
      description: 'Reemplaza el visual superior de la landing.',
    },
    {
      id: 'brand_bridge',
      key: 'brand_bridge',
      title: 'Bloque contigo',
      alt_text: 'Ilustración de acompañamiento de marca',
      description: 'Reemplaza el visual del bloque de acompañamiento.',
    },
    {
      id: 'support',
      key: 'support',
      title: 'Metodología / soporte',
      alt_text: 'Ilustración de metodología y soporte',
      description: 'Reemplaza el visual del bloque de metodología.',
    },
  ],
  testimonials: [
    {
      id: 'client_photo',
      key: 'testimonial_client_photo',
      title: 'Foto de cliente o caso',
      alt_text: 'Foto real de cliente, proyecto o caso',
      description: 'Usa una imagen real para reforzar confianza en testimonios.',
    },
  ],
};

export const LANDING_SECTION_HELP = {
  applications: {
    appears: 'Cotización pública -> Aplicativos de marca',
    copy: 'Sube fotos reales de aplicaciones: tarjetas, empaques, redes, papelería, uniformes, señalética o web. Estas imágenes aparecen en la sección de aplicativos de los paquetes.',
    recommendation: 'Formato horizontal o cuadrado, ideal WEBP/JPG, máximo 5MB.',
  },
  illustrations: {
    appears: 'Cotización pública -> Ilustraciones de landing',
    copy: 'Usa esta sección para reemplazar visuales decorativos o ilustraciones de bloques específicos de la cotización.',
    recommendation: 'Usa PNG/WEBP con buen recorte y fondo limpio.',
  },
  projects: {
    appears: 'Cotización pública -> Carrusel de proyectos (entre Paquetes y "Un equipo…")',
    copy: 'Sube 5-6 fotos de tus mejores proyectos. Se muestran en un carrusel de ancho completo, sin texto encima: la imagen habla. El "Orden visual" define la secuencia (1, 2, 3…). Si no cargas ninguna, la sección simplemente no aparece.',
    recommendation: 'Horizontal 16:9, ideal WEBP ~1600-2000px de ancho, máximo 5MB.',
  },
  testimonials: {
    appears: 'Cotización pública -> Testimonios / casos',
    copy: 'Usa fotos reales de cliente, proyecto o caso. Evita imágenes genéricas.',
    recommendation: 'Formato cuadrado o retrato suave, ideal WEBP/JPG, máximo 5MB.',
  },
};

export const LANDING_APPLICATION_TARGETS = [
  { id: 'starter', label: 'Starter', target: 3, pkg: { id: 'branding_starter', nombre: 'Brand Starter' } },
  { id: 'select', label: 'Select', target: 5, pkg: { id: 'branding_identity', nombre: 'Brand Identity' } },
  { id: 'premium', label: 'Premium', target: 10, pkg: { id: 'branding_legacy', nombre: 'Brand Legacy' } },
];

export const LANDING_ASSET_COLUMNS = new Set([
  'id',
  'key',
  'section',
  'title',
  'description',
  'alt_text',
  'package_scope',
  'storage_bucket',
  'storage_path',
  'public_url',
  'mime_type',
  'size_bytes',
  'sort_order',
  'active',
  'created_by',
  'created_at',
  'updated_at',
]);

export const LANDING_TESTIMONIAL_COLUMNS = new Set([
  'id',
  'client_name',
  'brand_name',
  'role_label',
  'testimonial',
  'photo_bucket',
  'photo_path',
  'photo_url',
  'project_id',
  'display_context',
  'sort_order',
  'active',
  'archived_at',
  'metadata',
  'created_by',
  'created_at',
  'updated_at',
]);

function normalizeText(value = '') {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function safeLandingAssetKey(value = '') {
  return normalizeText(value)
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'asset';
}

export function safeLandingAssetFileName(value = 'asset') {
  return String(value || 'asset')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9._-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'asset';
}

export function normalizePackageScope(value) {
  if (Array.isArray(value)) {
    return value.map(item => safeLandingAssetKey(item)).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .replace(/[{}"]/g, '')
      .split(',')
      .map(item => safeLandingAssetKey(item))
      .filter(Boolean);
  }
  return [];
}

export function expandLandingPackageScope(value = []) {
  const scope = normalizePackageScope(value);
  const expanded = new Set(scope);
  for (const option of PACKAGE_SCOPE_OPTIONS) {
    if (scope.includes(option.id) || option.aliases?.some(alias => scope.includes(safeLandingAssetKey(alias)))) {
      (option.aliases || [option.id]).forEach(alias => expanded.add(safeLandingAssetKey(alias)));
    }
  }
  return Array.from(expanded);
}

export function landingPackageScopeDisplay(scope = []) {
  const normalized = normalizePackageScope(scope);
  if (!normalized.length) return 'Todos los paquetes';
  const labels = [];
  for (const group of LANDING_PACKAGE_SCOPE_GROUPS) {
    for (const option of group.options) {
      const aliases = (option.aliases || [option.id]).map(safeLandingAssetKey);
      if (aliases.some(alias => normalized.includes(alias)) && !labels.includes(option.label)) {
        labels.push(option.label);
      }
    }
  }
  return labels.length ? labels.join(', ') : normalized.join(', ');
}

export function landingAssetPresetsForSection(section = '') {
  return LANDING_ASSET_PRESETS[section] || [];
}

export function normalizeLandingAsset(row = {}) {
  const section = LANDING_ASSET_SECTIONS.some(item => item.id === row.section) ? row.section : 'illustrations';
  const title = String(row.title || '').trim();
  const key = safeLandingAssetKey(row.key || title || section);
  return {
    ...row,
    key,
    section,
    title,
    description: row.description || '',
    alt_text: row.alt_text || title || key,
    package_scope: normalizePackageScope(row.package_scope),
    storage_bucket: row.storage_bucket || LANDING_ASSET_BUCKET,
    storage_path: row.storage_path || '',
    public_url: row.public_url || '',
    mime_type: row.mime_type || '',
    size_bytes: Number(row.size_bytes || 0),
    sort_order: Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : 0,
    active: row.active !== false,
  };
}

export function filterLandingAssetPayload(row = {}) {
  return Object.fromEntries(
    Object.entries(row).filter(([key, value]) => LANDING_ASSET_COLUMNS.has(key) && value !== undefined)
  );
}

export function normalizeLandingTestimonial(row = {}) {
  const clientName = String(row.client_name || row.clientName || row.public_name || '').trim();
  const brandName = String(row.brand_name || row.brandName || '').trim();
  const roleLabel = String(row.role_label || row.roleLabel || '').trim();
  const testimonial = String(row.testimonial || row.testimonial_text || row.description || '').trim();
  return {
    ...row,
    client_name: clientName,
    brand_name: brandName,
    role_label: roleLabel,
    testimonial,
    photo_bucket: row.photo_bucket || row.storage_bucket || LANDING_ASSET_BUCKET,
    photo_path: row.photo_path || row.storage_path || '',
    photo_url: row.photo_url || row.public_url || '',
    project_id: row.project_id || row.proyecto_id || null,
    display_context: row.display_context || 'landing',
    sort_order: Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : 0,
    active: row.active !== false && !row.archived_at,
    archived_at: row.archived_at || null,
    metadata: row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata) ? row.metadata : {},
  };
}

export function filterLandingTestimonialPayload(row = {}) {
  return Object.fromEntries(
    Object.entries(row).filter(([key, value]) => LANDING_TESTIMONIAL_COLUMNS.has(key) && value !== undefined)
  );
}

export function landingAssetPublicUrl(asset = {}) {
  if (!asset) return '';
  if (asset.public_url) return asset.public_url;
  if (!asset.storage_bucket || !asset.storage_path || !isConfigured) return '';
  const { data } = supabase.storage.from(asset.storage_bucket).getPublicUrl(asset.storage_path);
  return data?.publicUrl || '';
}

export function landingAssetsForSection(assets = [], section = '') {
  return (assets || [])
    .map(normalizeLandingAsset)
    .filter(asset => asset.active && asset.section === section)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.title.localeCompare(b.title));
}

export async function fetchPublicLandingAssets() {
  if (!isConfigured) return [];
  const { data, error } = await publicSupabase
    .from('landing_assets')
    .select('*')
    .eq('active', true)
    .order('section', { ascending: true })
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []).map(normalizeLandingAsset);
}

export function landingTestimonialPhotoUrl(testimonial = {}) {
  if (!testimonial) return '';
  if (testimonial.photo_url) return testimonial.photo_url;
  if (!testimonial.photo_bucket || !testimonial.photo_path || !isConfigured) return '';
  const { data } = supabase.storage.from(testimonial.photo_bucket).getPublicUrl(testimonial.photo_path);
  return data?.publicUrl || '';
}

export function applicationCatalogImageUrl(application = {}) {
  if (!application) return '';
  if (application.image_url) return application.image_url;
  if (!application.image_bucket || !application.image_path || !isConfigured) return '';
  const { data } = supabase.storage.from(application.image_bucket).getPublicUrl(application.image_path);
  return data?.publicUrl || '';
}

export async function fetchPublicLandingTestimonials() {
  if (!isConfigured) return [];
  const { data, error } = await publicSupabase
    .from('landing_testimonials')
    .select('id, client_name, brand_name, role_label, testimonial, photo_bucket, photo_path, photo_url, project_id, display_context, sort_order, active, archived_at, metadata, created_at, updated_at')
    .eq('active', true)
    .is('archived_at', null)
    .in('display_context', ['landing', 'quote', 'all'])
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(normalizeLandingTestimonial);
}

export function firstLandingAsset(assets = [], section = '', keys = []) {
  const keySet = new Set((Array.isArray(keys) ? keys : [keys]).map(safeLandingAssetKey).filter(Boolean));
  const rows = landingAssetsForSection(assets, section);
  if (!keySet.size) return rows[0] || null;
  return rows.find(asset => keySet.has(safeLandingAssetKey(asset.key))) || null;
}

function packageScopeCandidates(pkg = {}) {
  const raw = [
    pkg.id,
    pkg.family,
    pkg.nombre,
    pkg.name,
    pkg.package_name,
  ].map(safeLandingAssetKey).filter(Boolean);
  const text = normalizeText(`${pkg.id || ''} ${pkg.nombre || ''} ${pkg.name || ''}`);
  const isCorporate = /corporate|ascend|essence/.test(text) || pkg.family === 'corporate';
  if (isCorporate) {
    raw.push('corporate');
    if (/essence/.test(text)) raw.push('essence', 'corporate_essence');
    if (/legacy/.test(text)) raw.push('corporate_legacy');
    if (/ascend/.test(text)) raw.push('ascend', 'corporate_ascend');
  } else {
    if (/starter|start/.test(text)) raw.push('starter', 'normal_starter', 'branding_starter');
    if (/select|identity/.test(text)) raw.push('select', 'identity', 'normal_select', 'branding_identity');
    if (/premium|legacy/.test(text)) raw.push('premium', 'normal_premium', 'branding_legacy');
  }
  return new Set(raw);
}

export function landingAssetMatchesPackage(asset = {}, pkg = {}) {
  const scope = normalizePackageScope(asset.package_scope);
  if (!scope.length) return true;
  const candidates = packageScopeCandidates(pkg);
  return scope.some(item => candidates.has(item));
}

export async function uploadLandingAssetFile(file, { section = 'illustrations', key = 'asset' } = {}) {
  if (!file) throw new Error('Selecciona una imagen para subir.');
  if (!LANDING_ASSET_MIME_TYPES.includes(file.type)) {
    throw new Error('Usa una imagen JPG, PNG, WEBP o AVIF. SVG no está habilitado para este gestor.');
  }
  if (file.size > LANDING_ASSET_MAX_BYTES) {
    throw new Error('La imagen supera 5MB. Sube una versión optimizada.');
  }
  if (!isConfigured) throw new Error('Supabase no está configurado para subir imágenes.');

  const safeSection = safeLandingAssetKey(section);
  const safeKey = safeLandingAssetKey(key);
  const baseName = safeLandingAssetFileName(file.name);
  const path = `${safeSection}/${safeKey}/${Date.now()}-${baseName}`;
  const { error } = await supabase.storage
    .from(LANDING_ASSET_BUCKET)
    .upload(path, file, {
      contentType: file.type,
      cacheControl: '31536000',
      upsert: false,
    });
  if (error) throw error;
  const { data } = supabase.storage.from(LANDING_ASSET_BUCKET).getPublicUrl(path);
  return {
    storage_bucket: LANDING_ASSET_BUCKET,
    storage_path: path,
    public_url: data?.publicUrl || '',
    mime_type: file.type,
    size_bytes: file.size,
  };
}

export async function uploadLandingTestimonialPhoto(file, { key = 'testimonial' } = {}) {
  const media = await uploadLandingAssetFile(file, { section: 'testimonials', key });
  return {
    photo_bucket: media.storage_bucket,
    photo_path: media.storage_path,
    photo_url: media.public_url,
  };
}

export async function uploadApplicationCatalogImage(file, { key = 'application' } = {}) {
  const media = await uploadLandingAssetFile(file, { section: 'applications', key });
  return {
    image_bucket: media.storage_bucket,
    image_path: media.storage_path,
    image_url: media.public_url,
  };
}
